/**
 * Normalize dynamic route segments so that paths like
 * /users/123 and /users/456 collapse to /users/:id.
 *
 * Used by PassiveLearner to deduplicate discovered routes.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_RE = /^\d+$/;
const MONGO_ID_RE = /^[0-9a-f]{24}$/i;
const SHORT_ID_RE = /^[A-Za-z0-9_-]{16,}$/; // nanoid, cuid, etc.
const HASH_RE = /^[0-9a-f]{32,}$/i; // md5, sha hashes

/**
 * Returns true if a path segment looks like a dynamic parameter
 * (numeric id, UUID, Mongo ObjectId, hash, or nanoid-style id).
 */
function isDynamicSegment(segment: string): boolean {
  if (!segment) return false;
  if (NUMERIC_RE.test(segment)) return true;
  if (UUID_RE.test(segment)) return true;
  if (MONGO_ID_RE.test(segment)) return true;
  if (HASH_RE.test(segment)) return true;
  // Short IDs (nanoid, cuid) — alphanumeric 16+ chars with no spaces
  if (SHORT_ID_RE.test(segment) && !/[a-z]{5,}/i.test(segment)) return true;
  return false;
}

/**
 * Normalize a pathname by replacing dynamic segments with `:id`.
 *
 * Examples:
 *   /users/123           → /users/:id
 *   /org/abc-def-123/settings → /org/:id/settings
 *   /dashboard           → /dashboard
 */
export function normalizeRoute(path: string): string {
  return path
    .split('/')
    .map((segment) => (isDynamicSegment(segment) ? ':id' : segment))
    .join('/');
}
